import { getNodeApiHeaders, getSocketUrl } from '@/helpers/constants';
import { io, Socket } from 'socket.io-client';

class SocketService {
  private socket: Socket | null = null;
  private isConnected = false;
  private connectionPromise: Promise<Socket | null> | null = null;

  // Connect to the socket server
  connect(serverUrl?: string) {
    // If already connected, return existing socket
    if (this.socket && this.socket.connected) {
      return this.socket;
    }

    // If there's a connection in progress, return the existing promise
    if (this.connectionPromise) {
      return this.socket; // Return current socket (might be null but will be updated)
    }

    // Use provided URL or auto-detect based on environment
    // getSocketUrl() returns base URL without /socket.io/ path
    const socketUrl = serverUrl || getSocketUrl();
    
    // Determine if we're in production
    const isProduction = window.location.hostname !== 'localhost' && 
                        window.location.hostname !== '127.0.0.1';

    // Create a new connection promise
    this.connectionPromise = new Promise((resolve) => {
      try {
        // Socket.IO client configuration
        // - Base URL should NOT include /socket.io/ path
        // - Path option should always be '/socket.io/' to match server configuration
        // - In production (AWS App Runner), prefer polling first as WebSocket upgrades can be unreliable
        //   Socket.IO will automatically upgrade to WebSocket if available
        this.socket = io(socketUrl, {
          // In production, try polling first (more reliable with AWS App Runner)
          // Socket.IO will automatically upgrade to WebSocket if the server supports it
          // In development, try WebSocket first for better performance
          transports: isProduction ? ['polling', 'websocket'] : ['websocket', 'polling'],
          timeout: 20000, // Increased timeout for production connections
          reconnection: true,
          reconnectionAttempts: 10,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
          forceNew: false,
          autoConnect: true,
          // Always use /socket.io/ path to match server configuration
          // Server is configured with path: '/socket.io/' in server/src/server.ts
          path: '/socket.io/',
          // Additional options for better compatibility with AWS App Runner
          upgrade: true, // Allow transport upgrades
          rememberUpgrade: false, // Don't remember upgrade preference (start fresh each time)
          // Resolve the current token for every initial connection/reconnection,
          // rather than capturing a stale token when this module is loaded.
          auth: (callback) => {
            const authorization = getNodeApiHeaders().Authorization;
            callback({ token: authorization?.replace(/^Bearer /, '') || '' });
          },
        });

        this.socket.on('connect', () => {
          this.isConnected = true;
          this.connectionPromise = null; // Clear the promise
          console.log('Socket.IO connected successfully:', this.socket?.id);
          resolve(this.socket);
        });

        this.socket.on('disconnect', () => {
          this.isConnected = false;
        });

        this.socket.on('connect_error', (error) => {
          this.isConnected = false;
          this.connectionPromise = null; // Clear the promise on error
          // Log connection error for debugging (especially in production)
          console.error('Socket.IO connection error:', error);
          console.error('Socket.IO connection URL:', socketUrl);
          console.error('Socket.IO path:', '/socket.io/');
          console.error('Socket.IO transport attempts:', this.socket?.io?.engine?.transport?.name || 'unknown');
          // Don't resolve to null immediately - let Socket.IO retry with different transports
          // The reconnection logic will handle retries
        });

        this.socket.on('reconnect', (attemptNumber) => {
          this.isConnected = true;
        });

        this.socket.on('reconnect_error', (error) => {
          // Log reconnection errors for debugging
          console.warn('Socket.IO reconnection error:', error);
        });
        
        // Log when transport changes
        this.socket.io?.engine?.on('upgrade', () => {
          console.log('Socket.IO transport upgraded to:', this.socket?.io?.engine?.transport?.name);
        });
        
        this.socket.io?.engine?.on('upgradeError', (error) => {
          console.warn('Socket.IO upgrade error (will continue with polling):', error);
        });

        // If socket is already connected, resolve immediately
        if (this.socket.connected) {
          this.isConnected = true;
          this.connectionPromise = null;
          resolve(this.socket);
        }

      } catch (error) {
        this.connectionPromise = null;
        resolve(null);
      }
    });

    return this.socket;
  }

  // Disconnect from the socket server
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;

    }
  }

  // Force a connection attempt and wait for it to be established
  async forceConnect(serverUrl?: string): Promise<Socket | null> {

    this.disconnect(); // Disconnect any existing connection
    
    // Start the connection
    const socket = this.connect(serverUrl);
    
    if (socket) {
      // Wait for the connection to be established
      const connected = await this.waitForConnection(10000);
      if (connected) {

        return this.socket;
      } else {

        return null;
      }
    }
    
    return null;
  }

  // Get the current socket instance
  getSocket(): Socket | null {
    return this.socket;
  }

  // Check if connected
  isSocketConnected(): boolean {
    return this.socket ? this.socket.connected : false;
  }

  // Get detailed connection status
  getConnectionStatus(): { connected: boolean; socketId?: string; hasSocket: boolean } {
    return {
      connected: this.socket ? this.socket.connected : false,
      socketId: this.socket?.id,
      hasSocket: !!this.socket
    };
  }

  // Wait for connection to be established
  async waitForConnection(timeout: number = 5000): Promise<boolean> {
    if (this.socket && this.socket.connected) {
      return true;
    }

    // If there's a connection promise in progress, wait for it
    if (this.connectionPromise) {
      try {
        await this.connectionPromise;
        return this.socket ? this.socket.connected : false;
      } catch (error) {
        return false;
      }
    }

    return new Promise((resolve) => {
      if (!this.socket) {
        resolve(false);
        return;
      }

      const timeoutId = setTimeout(() => {
              resolve(false);
      }, timeout);

      if (this.socket.connected) {
        clearTimeout(timeoutId);
        resolve(true);
        return;
      }

      const onConnect = () => {
        clearTimeout(timeoutId);
        this.socket?.off('connect', onConnect);
        resolve(true);
      };

      this.socket.on('connect', onConnect);
    });
  }

  // Listen for specific events
  on(event: string, callback: (...args: any[]) => void) {
    if (this.socket) {
      this.socket.on(event, callback);
    }
  }

  // Remove event listener
  off(event: string, callback?: (...args: any[]) => void) {
    if (this.socket) {
      if (callback) {
        this.socket.off(event, callback);
      } else {
        this.socket.off(event);
      }
    }
  }

  // Ensure connection is established before proceeding
  async ensureConnection(): Promise<boolean> {
    if (this.socket && this.socket.connected) {
      return true;
    }

    // If no socket exists, try to connect
    if (!this.socket) {
      this.connect();
    }

    // Wait for connection with a reasonable timeout
    return this.waitForConnection(5000);
  }

  // Emit an event
  emit(event: string, data?: any) {
    if (this.socket && this.isConnected) {
      this.socket.emit(event, data);
    }
  }

  // Force emit an event (useful for race conditions)
  forceEmit(event: string, data?: any): boolean {
    if (this.socket) {
      try {
        this.socket.emit(event, data);
    
        return true;
      } catch (error) {
        return false;
      }
    }
    return false;
  }

  // Send socket message using common shay_realtime event
  async sendSocketMessage(eventData: { type: number; data: any }): Promise<boolean> {
    try {
      if (this.socket && this.isConnected) {
        this.socket.emit('shay_realtime', eventData);
        return true;
      } else {
        // Try to connect if not connected
        const socket = this.connect();
        if (socket && this.isConnected && this.socket) {
          this.socket.emit('shay_realtime', eventData);
          return true;
        } else {
          return false;
        }
      }
    } catch (error) {
      return false;
    }
  }
}

// Create and export a singleton instance
const socketService = new SocketService();
export default socketService;
